import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import { clearExamData } from '../../utils/helpers';

const Login = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationLink, setVerificationLink] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const navigate = useNavigate();

  // 🧹 Bersihkan data ujian hanya kalau bukan mode demo
  useEffect(() => {
    const demoMode = localStorage.getItem('demo_mode');
    if (demoMode !== 'true') {
      clearExamData();
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  // 🔑 Handle resend verification
  const handleResendVerification = async () => {
    if (!form.email) return;
    
    setResendLoading(true);
    setResendSuccess('');
    
    try {
      const res = await axios.post('/auth/resend-verification', {
        email: form.email.trim()
      });
      
      setResendSuccess('Link verifikasi baru telah dikirim! Cek email Anda.');
      
      // Untuk development, tampilkan link
      if (res.data.verificationLink && process.env.NODE_ENV !== 'production') {
        setVerificationLink(res.data.verificationLink);
        console.log('🔗 New Verification Link:', res.data.verificationLink);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal mengirim ulang verifikasi');
    } finally {
      setResendLoading(false);
    }
  };

  // 🔑 Login submit - UPDATED
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setVerificationRequired(false);
    setResendSuccess('');
    setLoading(true);

    try {
      if (!form.email || !form.password) {
        setError('Email dan password wajib diisi');
        setLoading(false);
        return;
      }

      console.log('🔵 Sending login request:', form.email);

      const res = await axios.post('/auth/login', {
        email: form.email.trim(),
        password: form.password.trim(),
      });

      console.log('🟢 Login response:', res.data);

      if (!res.data?.token) throw new Error('Token tidak diterima dari server.');

      // ✅ JIKA BERHASIL LOGIN
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.removeItem('demo_mode'); // pastikan bukan demo

      clearExamData();

      const role = res.data.user.role?.toLowerCase();
      console.log('👤 User role:', role);

      if (role === 'admin' || role === 'super_admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
      
    } catch (err) {
      console.error('❌ Login failed:', err.response?.data || err.message);
      const errorData = err.response?.data || {};
      
      // ✅ CASE 1: EMAIL BELUM DIVERIFIKASI
      if (errorData.requiresVerification) {
        setVerificationRequired(true);
        setError('Email belum diverifikasi. Silakan cek email Anda.');
        
        // Simpan link untuk development
        if (errorData.verificationLink) {
          setVerificationLink(errorData.verificationLink);
          console.log('🔗 Verification Link from login:', errorData.verificationLink);
        }
      }
      // ✅ CASE 2: ERROR LAIN
      else {
        const msg = errorData.message || err.message || 'Server error. Coba lagi.';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle test verification link (development only)
  const handleTestVerification = () => {
    if (verificationLink) {
      window.open(verificationLink, '_blank');
    }
  };

  // Kembali ke form login
  const handleBackToLogin = () => {
    setVerificationRequired(false);
    setVerificationLink('');
    setResendSuccess('');
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* LOGO DAN JUDUL */}
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <img 
              src="/img/proest.png" 
              alt="PRO-EST Logo" 
              style={styles.logo}
              onError={(e) => {
                console.error('❌ Logo not found:', e.target.src);
                e.target.style.display = 'none';
                
                const placeholder = document.createElement('div');
                placeholder.style.cssText = `
                  width: 70px;
                  height: 70px;
                  background: linear-gradient(135deg, #1a3c6d, #4a90e2);
                  border-radius: 12px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: white;
                  font-weight: bold;
                  font-size: 28px;
                  box-shadow: 0 4px 12px rgba(26, 60, 109, 0.3);
                `;
                placeholder.textContent = 'P';
                
                const parent = e.target.parentNode;
                parent.innerHTML = '';
                parent.appendChild(placeholder);
              }}
            />
          </div>
          <div style={styles.titleContainer}>
            <h2 style={styles.title}>PRO-EST</h2>
            <p style={styles.subtitle}>Proficiency in English Skills Test</p>
          </div>
        </div>

        {/* ERROR MESSAGE */}
        {error && <p style={styles.error}>{error}</p>}
        {resendSuccess && <p style={styles.success}>{resendSuccess}</p>}

        {!verificationRequired ? (
          // FORM LOGIN NORMAL
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              style={styles.input}
              required
              disabled={loading}
              autoComplete="email"
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              style={styles.input}
              required
              disabled={loading}
              autoComplete="current-password"
            />
            <button
              type="submit"
              style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        ) : (
          // VERIFICATION REQUIRED MESSAGE
          <div style={styles.verificationBox}>
            <div style={styles.verificationIcon}>⚠️</div>
            <h3 style={styles.verificationTitle}>Verifikasi Diperlukan</h3>
            <p style={styles.verificationText}>
              Email <strong>{form.email}</strong> belum diverifikasi.
            </p>
            <p style={styles.verificationSubtext}>
              Silakan cek folder <strong>Inbox</strong> atau <strong>Spam</strong> di email Anda untuk link verifikasi.
            </p>

            <div style={styles.verificationActions}>
              <button 
                onClick={handleResendVerification}
                style={styles.secondaryButton}
                disabled={resendLoading}
              >
                {resendLoading ? 'Mengirim...' : 'Kirim Ulang Verifikasi'}
              </button>
              
              {/* DEVELOPMENT FEATURES */}
              {process.env.NODE_ENV !== 'production' && verificationLink && (
                <button 
                  onClick={handleTestVerification}
                  style={styles.devButton}
                >
                  Test Verification Link (Dev)
                </button>
              )}
              
              <button 
                onClick={handleBackToLogin}
                style={styles.textButton}
              >
                ← Kembali ke Login
              </button>
            </div>
          </div>
        )}

        {/* LINKS */}
        <div style={styles.linksContainer}>
          <p style={styles.link}>
            Don't have an account?{' '}
            <a href="/register" style={styles.a}>
              Register here
            </a>
          </p>

          <p style={styles.link}>
            <a href="/forgot-password" style={styles.a}>
              Lupa Kata Sandi?
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// 🎨 Style Section - TAMBAHKAN STYLE VERIFICATION
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a3c6d, #4a90e2)',
    fontFamily: '"Segoe UI", sans-serif',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    padding: '40px 30px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '30px',
    gap: '20px',
    flexWrap: 'wrap',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '70px',
  },
  logo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(26, 60, 109, 0.3)',
    border: '2px solid #f0f0f0',
  },
  titleContainer: {
    textAlign: 'left',
    flex: 1,
    minWidth: '200px',
  },
  title: {
    margin: 0,
    marginBottom: '5px',
    fontSize: '34px',
    color: '#1a3c6d',
    fontWeight: '800',
    lineHeight: '1.1',
    letterSpacing: '0.5px',
  },
  subtitle: {
    margin: 0,
    color: '#4a90e2',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '15px',
    margin: '12px 0',
    borderRadius: '10px',
    border: '1px solid #ddd',
    fontSize: '16px',
    boxSizing: 'border-box',
    transition: 'all 0.3s',
    ':focus': {
      outline: 'none',
      borderColor: '#1a3c6d',
      boxShadow: '0 0 0 3px rgba(26, 60, 109, 0.1)',
    }
  },
  button: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #1a3c6d, #2a5caa)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '15px',
    transition: 'all 0.3s',
    boxShadow: '0 4px 12px rgba(26, 60, 109, 0.3)',
    ':hover': {
      background: 'linear-gradient(135deg, #15325a, #1a3c6d)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(26, 60, 109, 0.4)',
    },
    ':active': {
      transform: 'translateY(0)',
    }
  },
  error: {
    color: '#e74c3c',
    background: '#fadad7',
    padding: '12px',
    borderRadius: '8px',
    margin: '15px 0',
    fontSize: '14px',
    border: '1px solid #f5c6cb',
  },
  success: {
    color: '#27ae60',
    background: '#d5f4e6',
    padding: '12px',
    borderRadius: '8px',
    margin: '15px 0',
    fontSize: '14px',
    border: '1px solid #c3e6cb',
  },
  linksContainer: {
    marginTop: '25px',
    borderTop: '1px solid #eee',
    paddingTop: '20px',
  },
  link: {
    margin: '10px 0',
    fontSize: '14px',
    color: '#555',
  },
  a: {
    color: '#1a3c6d',
    textDecoration: 'none',
    fontWeight: 'bold',
    transition: 'color 0.2s',
    padding: '2px 4px',
    borderRadius: '4px',
    ':hover': {
      color: '#4a90e2',
      textDecoration: 'underline',
    }
  },
  // VERIFICATION BOX STYLES
  verificationBox: {
    textAlign: 'center',
    padding: '20px 0',
  },
  verificationIcon: {
    fontSize: '48px',
    marginBottom: '15px',
  },
  verificationTitle: {
    margin: '0 0 10px 0',
    fontSize: '20px',
    color: '#1a3c6d',
    fontWeight: 'bold',
  },
  verificationText: {
    margin: '0 0 10px 0',
    color: '#333',
    fontSize: '14px',
  },
  verificationSubtext: {
    margin: '0 0 20px 0',
    color: '#666',
    fontSize: '13px',
  },
  verificationActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '20px',
  },
  secondaryButton: {
    width: '100%',
    padding: '14px',
    background: '#4a90e2',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.3s',
    ':hover': {
      background: '#357ae8',
    }
  },
  devButton: {
    width: '100%',
    padding: '12px',
    background: '#f39c12',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
    ':hover': {
      background: '#e67e22',
    }
  },
  textButton: {
    background: 'none',
    border: 'none',
    color: '#1a3c6d',
    cursor: 'pointer',
    fontSize: '13px',
    textDecoration: 'underline',
    marginTop: '10px',
  },
};

// Tambahkan CSS untuk focus state
const addFocusStyles = () => {
  if (typeof window === 'undefined') return;
  
  const style = document.createElement('style');
  style.textContent = `
    input:focus {
      outline: none;
      border-color: #1a3c6d !important;
      box-shadow: 0 0 0 3px rgba(26, 60, 109, 0.1) !important;
    }
    
    button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    button:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(26, 60, 109, 0.4) !important;
    }
    
    a:hover {
      color: #4a90e2 !important;
      text-decoration: underline !important;
    }
  `;
  document.head.appendChild(style);
};

// Panggil fungsi untuk menambahkan CSS
if (typeof window !== 'undefined') {
  addFocusStyles();
}

export default Login;
